import type { ExtendedUploadFile } from '@/components/FileUpload';
import { getMediaDuration } from '@/components/FileUpload/common/utils/getMediaDuration';
import { AIFunction } from '@/utils/const';
import { t } from '@/utils/helper/intl';

import { getBasePromptConfig } from './common';
import { ModelIdEnum } from './constants';
import { transformSeedanceReferenceVideosForCredit } from './utils/seedanceReferenceVideosCreditTransform';

const SEEDANCE_REFERENCE_PROMPT_PLACEHOLDER = 'fc.2e55e3d3.seedance20.promptPlaceholder';
const SEEDANCE_REFERENCE_IMAGES_LABEL = 'fc.74864f8a.seedance20.referenceImages';
const SEEDANCE_REFERENCE_VIDEOS_LABEL = 'fc.618c2ec0.seedance20.referenceVideos';
const SEEDANCE_REFERENCE_AUDIOS_LABEL = 'fc.1f4a4bb2.seedance20.referenceAudios';
const SEEDANCE_REFERENCE_IMAGES_TIPS = 'fc.9cf0f53d.seedance20.referenceImagesTips';
const SEEDANCE_REFERENCE_VIDEOS_TIPS = 'fc.3d4f8b21.seedance20.referenceVideosTips';
const SEEDANCE_REFERENCE_AUDIOS_TIPS = 'fc.b8bc17f4.seedance20.referenceAudiosTips';
const SEEDANCE_REFERENCE_VIDEOS_TOTAL_DURATION_ERROR =
  'fc.f2079847.seedance20.referenceVideos.totalDuration';
const SEEDANCE_REFERENCE_AUDIOS_TOTAL_DURATION_ERROR =
  'fc.721af7af.seedance20.referenceAudios.totalDuration';
const SEEDANCE_BADGE = 'fc.0fecc453.seedance20.badge';
const SEEDANCE_DESCRIPTION = 'fc.3b6d0d4b.seedance20.modelDescription';

const createMaxTotalDurationValidator =
  (maxDuration: number, errorKey: string) =>
  async (file: File, fileList: ExtendedUploadFile[]) => {
    const currentTotalDuration = fileList.reduce((total, currentFile) => {
      if (currentFile.status !== 'done' || typeof currentFile.duration !== 'number') {
        return total;
      }

      return total + currentFile.duration;
    }, 0);

    const nextFileDuration = await getMediaDuration(file);

    if (currentTotalDuration + nextFileDuration > maxDuration) {
      return t(errorKey, { maxDuration });
    }
  };

export const textToVideoModelConfig = [
  {
    modelName: 'Seedance 2.0',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Seedance_v2_0,
    iconUrl: '/images/seedance2.0.png',
    badges: [SEEDANCE_BADGE],
    description: SEEDANCE_DESCRIPTION,
    retainPrompt: false,
    proOnly: true,
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video, {
        type: 'mentionPrompt',
        config: {
          maxLength: 2000,
          rows: 3,
          trackSource: AIFunction.Text2Video,
          mentionFields: ['reference_images', 'reference_videos', 'reference_audios'],
          placeholder: SEEDANCE_REFERENCE_PROMPT_PLACEHOLDER,
        },
      }),
      {
        type: 'file',
        value: [],
        label: SEEDANCE_REFERENCE_IMAGES_LABEL,
        key: 'reference_images',
        config: {
          type: 'image',
          accept: '.jpg,.jpeg,.png,.webp,.gif',
          tips: SEEDANCE_REFERENCE_IMAGES_TIPS,
          maxCount: 9,
          allowLibrarySelect: true,
          multiple: true,
          feature: AIFunction.Text2Video,
          resourceOption: 'Reference Images',
          rules: [
            {
              maxSize: 30 * 1024 * 1024,
              minResolution: '300*300',
              maxResolution: '6000*6000',
            },
            {
              minAspectRatio: '1:2.5',
              maxAspectRatio: '2.5:1',
            },
          ],
        },
      },
      {
        type: 'file',
        value: [],
        label: SEEDANCE_REFERENCE_VIDEOS_LABEL,
        key: 'reference_videos',
        credit: true,
        creditTransform: transformSeedanceReferenceVideosForCredit,
        config: {
          type: 'video',
          accept: '.mp4,video/mp4,.mov,video/quicktime',
          tips: SEEDANCE_REFERENCE_VIDEOS_TIPS,
          maxCount: 3,
          allowLibrarySelect: true,
          multiple: true,
          feature: AIFunction.Text2Video,
          resourceOption: 'Reference Videos',
          rules: [
            {
              maxSize: 50 * 1024 * 1024,
              minDuration: 2,
              maxDuration: 15,
              minResolution: '480p',
              maxResolution: '1080p',
              validator: createMaxTotalDurationValidator(
                15,
                SEEDANCE_REFERENCE_VIDEOS_TOTAL_DURATION_ERROR,
              ),
            },
          ],
        },
      },
      {
        type: 'file',
        value: [],
        label: SEEDANCE_REFERENCE_AUDIOS_LABEL,
        key: 'reference_audios',
        config: {
          type: 'audio',
          accept: '.wav,.mp3',
          tips: SEEDANCE_REFERENCE_AUDIOS_TIPS,
          maxCount: 3,
          allowLibrarySelect: false,
          multiple: true,
          feature: AIFunction.Text2Video,
          resourceOption: 'Reference Audios',
          rules: [
            {
              maxSize: 15 * 1024 * 1024,
              minDuration: 2,
              maxDuration: 15,
              validator: createMaxTotalDurationValidator(
                15,
                SEEDANCE_REFERENCE_AUDIOS_TOTAL_DURATION_ERROR,
              ),
            },
          ],
        },
      },
      {
        type: 'AspectRatio',
        value: '16:9',
        label: '52e3e59f1db1469317e7aa55d9d670e4',
        key: 'aspect_ratio',
        config: {
          options: [
            { label: '16:9', value: '16:9' },
            { label: '9:16', value: '9:16' },
            { label: '4:3', value: '4:3' },
            { label: '3:4', value: '3:4' },
            { label: '1:1', value: '1:1' },
            { label: '21:9', value: '21:9' },
          ],
        },
      },
      {
        type: 'radio',
        value: '720p',
        label: 'merror.export.resolution',
        key: 'resolution',
        credit: true,
        config: {
          options: [
            { label: '720p', value: '720p' },
            { label: '1080p', value: '1080p' },
          ],
        },
      },
      {
        type: 'durationSlider',
        value: 5,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          min: 4,
          max: 15,
          step: 1,
          unit: 's',
        },
      },
      {
        type: 'checkbox',
        label: 'fc.450c06f7',
        key: 'enable_web_search',
        value: false,
        advanced: true,
        config: {
          label: t('fc.44dcef4d'),
        },
      },
    ],
  },
  {
    modelName: 'Kling v3.0 Pro',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Kling_v3_0_Pro,
    iconUrl: '/images/kling2.6pro.png',
    proOnly: true,
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video),
      {
        type: 'AspectRatio',
        value: '1:1',
        label: '52e3e59f1db1469317e7aa55d9d670e4',
        key: 'aspect_ratio',
        config: {
          options: [
            { label: '9:16', value: '9:16' },
            { label: '16:9', value: '16:9' },
            { label: '1:1', value: '1:1' },
          ],
        },
      },
      {
        type: 'durationSlider',
        value: 5,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          min: 3,
          max: 15,
          step: 1,
          unit: 's',
        },
      },
      {
        type: 'switch',
        label: '359830b6b443f2a3fae314a287685809',
        key: 'sound',
        value: true,
        credit: true,
      },
    ],
  },
  {
    modelName: 'Kling v2.6 Pro',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Kling_v2_6_Pro,
    iconUrl: '/images/kling2.6pro.png',
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video),
      {
        type: 'AspectRatio',
        value: '1:1',
        label: '52e3e59f1db1469317e7aa55d9d670e4',
        key: 'aspect_ratio',
        config: {
          options: [
            { label: '9:16', value: '9:16' },
            { label: '16:9', value: '16:9' },
            { label: '1:1', value: '1:1' },
          ],
        },
      },
      {
        type: 'radio',
        value: 5,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          options: [
            { label: '5s', value: 5 },
            { label: '10s', value: 10 },
          ],
        },
      },
      {
        type: 'switch',
        label: '359830b6b443f2a3fae314a287685809',
        key: 'sound',
        value: true,
        credit: true,
      },
    ],
  },
  {
    modelName: 'Seedance v1.5 Pro',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Seedance_v1_5_Pro,
    iconUrl: '/images/seedance1.5pro.png',
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video),
      {
        type: 'AspectRatio',
        value: '16:9',
        label: '52e3e59f1db1469317e7aa55d9d670e4',
        key: 'aspect_ratio',
        config: {
          options: [
            { label: '21:9', value: '21:9' },
            { label: '16:9', value: '16:9' },
            { label: '4:3', value: '4:3' },
            { label: '1:1', value: '1:1' },
            { label: '3:4', value: '3:4' },
            { label: '9:16', value: '9:16' },
          ],
        },
      },
      {
        type: 'durationSlider',
        value: 5,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          min: 4,
          max: 12,
          step: 1,
          unit: 's',
        },
      },
      {
        type: 'radio',
        value: '720p',
        label: 'merror.export.resolution',
        key: 'resolution',
        credit: true,
        config: {
          options: [
            { label: '720p', value: '720p' },
            { label: '480p', value: '480p' },
          ],
        },
      },
      {
        type: 'checkbox',
        label: 'merror.0.8.lipsync.generatorAudio',
        key: 'generate_audio',
        value: true,
        credit: true,
        advanced: true,
        config: {
          label: t('fc.33302773'),
        },
      },
      {
        type: 'checkbox',
        label: 'fc.8d436b83',
        key: 'camera_fixed',
        value: false,
        advanced: true,
        config: {
          label: t('fc.b16ac978'),
        },
      },
    ],
  },
  {
    modelName: 'Sora 2 Pro',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Sora_2_Pro,
    iconUrl: '/images/sora2.png',
    proOnly: true,
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video),
      {
        type: 'radio',
        value: 4,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          options: [
            { label: '4s', value: 4 },
            { label: '8s', value: 8 },
            { label: '12s', value: 12 },
          ],
        },
      },
      {
        type: 'radio',
        value: '720*1280',
        label: 'merror.export.resolution',
        key: 'size',
        credit: true,
        config: {
          options: [
            { label: '720*1280', value: '720*1280' },
            { label: '1280*720', value: '1280*720' },
            { label: '1024*1792', value: '1024*1792' },
            { label: '1792*1024', value: '1792*1024' },
          ],
        },
      },
    ],
  },
  {
    modelName: 'Veo 3.1',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Veo_3_1,
    iconUrl: '/images/veo3.png',
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video),
      {
        type: 'AspectRatio',
        value: '9:16',
        label: '52e3e59f1db1469317e7aa55d9d670e4',
        key: 'aspect_ratio',
        config: {
          options: [
            { label: '9:16', value: '9:16' },
            { label: '16:9', value: '16:9' },
          ],
        },
      },
      {
        type: 'radio',
        value: 4,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          options: [
            { label: '4s', value: 4 },
            { label: '6s', value: 6 },
            { label: '8s', value: 8 },
          ],
        },
      },
      {
        type: 'radio',
        value: '720p',
        label: 'merror.export.resolution',
        key: 'resolution',
        config: {
          options: [
            { label: '720p', value: '720p' },
            { label: '1080p', value: '1080p' },
          ],
        },
      },
      {
        type: 'switch',
        label: '359830b6b443f2a3fae314a287685809',
        key: 'generate_audio',
        value: false,
        credit: true,
      },
    ],
  },
  {
    modelName: 'Kling 2.5 Turbo Pro',
    modelValue: ModelIdEnum.TEXT_TO_VIDEO_Kling_2_5_Turbo_Pro,
    iconUrl: '/images/kling.png',
    formConfig: [
      getBasePromptConfig(AIFunction.Text2Video),
      {
        type: 'AspectRatio',
        value: '9:16',
        label: '52e3e59f1db1469317e7aa55d9d670e4',
        key: 'aspect_ratio',
        config: {
          options: [
            { label: '9:16', value: '9:16' },
            { label: '16:9', value: '16:9' },
            { label: '1:1', value: '1:1' },
          ],
        },
      },
      {
        type: 'radio',
        value: 5,
        label: 'merror.assets.duration',
        key: 'duration',
        credit: true,
        config: {
          options: [
            { label: '5s', value: 5 },
            { label: '10s', value: 10 },
          ],
        },
      },
    ],
  },
];
